import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getLanguages, createLanguage, updateLanguage } from '../../api';
import { PageHeader, EmptyState, StatusBadge, TableSkeleton } from '../../components/ui';
import Modal from '../../components/ui/Modal';
import { Plus, Pencil, Globe, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

const defaultForm = { code: '', name: '', nativeName: '', flag: '', isActive: true, isDefault: false, order: 0 };

export default function LanguagesPage() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState<any>(defaultForm);

  const { data, isLoading } = useQuery({ queryKey: ['admin-languages'], queryFn: () => getLanguages().then((r) => r.data.data.languages) });

  const saveMutation = useMutation({
    mutationFn: (d: any) => editing ? updateLanguage(editing._id, d) : createLanguage(d),
    onSuccess: () => { toast.success(editing ? 'Language updated' : 'Language added'); qc.invalidateQueries({ queryKey: ['admin-languages'] }); closeModal(); },
  });

  const openCreate = () => { setEditing(null); setForm(defaultForm); setModal(true); };
  const openEdit = (l: any) => {
    setEditing(l);
    setForm({ code: l.code, name: l.name, nativeName: l.nativeName, flag: l.flag || '', isActive: l.isActive, isDefault: l.isDefault, order: l.order });
    setModal(true);
  };
  const closeModal = () => { setModal(false); setEditing(null); setForm(defaultForm); };

  const languages = data || [];

  return (
    <div className="p-6">
      <PageHeader title="Languages" subtitle="Manage supported app languages">
        <Link to="/language/translations" className="btn-secondary btn-sm">Translations</Link>
        <button onClick={openCreate} className="btn-primary"><Plus size={15} /> Add Language</button>
        <button onClick={() => qc.invalidateQueries({ queryKey: ['admin-languages'] })} className="btn-secondary btn-sm"><RefreshCw size={14} /></button>
      </PageHeader>

      <div className="card overflow-hidden">
        {isLoading ? <div className="p-5"><TableSkeleton rows={4} cols={5} /></div> : languages.length === 0 ? (
          <EmptyState message="No languages configured" icon={<Globe size={32} />} />
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="table-th">Flag</th>
                <th className="table-th">Code</th>
                <th className="table-th">English Name</th>
                <th className="table-th">Native Name</th>
                <th className="table-th">Default</th>
                <th className="table-th">Order</th>
                <th className="table-th">Status</th>
                <th className="table-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {languages.map((l: any) => (
                <tr key={l._id} className="table-row">
                  <td className="table-td text-2xl">{l.flag || '🌐'}</td>
                  <td className="table-td font-mono font-bold text-primary-600 uppercase">{l.code}</td>
                  <td className="table-td font-medium text-sm">{l.name}</td>
                  <td className="table-td text-sm">{l.nativeName}</td>
                  <td className="table-td">{l.isDefault ? <span className="badge badge-green">Default</span> : '—'}</td>
                  <td className="table-td text-xs">{l.order}</td>
                  <td className="table-td"><StatusBadge status={l.isActive ? 'active' : 'inactive'} /></td>
                  <td className="table-td">
                    <button onClick={() => openEdit(l)} className="p-1.5 rounded hover:bg-gray-100 text-blue-500"><Pencil size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={modal} onClose={closeModal} title={editing ? 'Edit Language' : 'Add Language'} size="sm">
        <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(form); }} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Code *</label><input required className="input uppercase" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase() })} placeholder="hi" disabled={!!editing} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Flag emoji</label><input className="input" value={form.flag} onChange={(e) => setForm({ ...form, flag: e.target.value })} placeholder="🇮🇳" /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Name (English) *</label><input required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Hindi" /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Native Name *</label><input required className="input" value={form.nativeName} onChange={(e) => setForm({ ...form, nativeName: e.target.value })} placeholder="हिंदी" /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Order</label><input type="number" className="input" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} /></div>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /> Active</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isDefault} onChange={(e) => setForm({ ...form, isDefault: e.target.checked })} /> Default</label>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={closeModal} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button type="submit" disabled={saveMutation.isPending} className="btn-primary flex-1 justify-center">{saveMutation.isPending ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
